/* eslint-disable max-len */
import {SERVICE_NAMES} from '../constants';
import {createApiCall} from '../utils';
import type {ServiceName} from '../constants';
import type {
    ApiCallOptions,
    PaginatedResponse,
    ConfigurationType,
    ConfigurationDefinitions,
    FontResource,
    ConfigurationResource,
    SecuritySettingsResource,
    CloneConfigurationRequest,
    CreateFontRequest,
    UpdateFontRequest,
    FontListParams
} from './api.d';

const createConfigurationApiCall = <T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string
) => createApiCall<T>(SERVICE_NAMES.CONFIGURATION as ServiceName, method, endpoint);

/**
 * Builds the account settings endpoint.
 * The `{language}` path segment is optional: the service exposes both the
 * language-scoped route (`.../settings/{type}/{language}`) and the language-less
 * route (`.../settings/{type}`). Omit `language` for the default (non-localized) settings.
 */
const getAccountSettingsEndpoint = (account: string, type: ConfigurationType, language?: string | null) =>
    `accounts/${account}/settings/${type}${language ? `/${language}` : ''}`;

/**
 * Builds the clone settings endpoint (`.../settings/{type}/clone/{language}`).
 * The `{language}` segment is optional, mirroring the settings routes.
 */
const getCloneSettingsEndpoint = (account: string, type: ConfigurationType, language?: string | null) =>
    `accounts/${account}/settings/${type}/clone${language ? `/${language}` : ''}`;

export const configurationApi = {
    // Configuration management
    getConfiguration: (account: string, type: ConfigurationType, options?: ApiCallOptions, language?: string | null) =>
        createConfigurationApiCall<ConfigurationResource>('GET', getAccountSettingsEndpoint(account, type, language))({...options}),

    updateConfiguration: (account: string, type: ConfigurationType, data: ConfigurationResource, options?: ApiCallOptions, language?: string | null) =>
        createConfigurationApiCall<ConfigurationResource>('PUT', getAccountSettingsEndpoint(account, type, language))({...options, data}),

    deleteConfiguration: (account: string, type: ConfigurationType, options?: ApiCallOptions, language?: string | null) =>
        createConfigurationApiCall<void>('DELETE', getAccountSettingsEndpoint(account, type, language))({...options}),

    cloneConfiguration: (account: string, type: ConfigurationType, data: CloneConfigurationRequest, options?: ApiCallOptions, language?: string | null) =>
        createConfigurationApiCall<ConfigurationResource>('POST', getCloneSettingsEndpoint(account, type, language))({...options, data}),

    // Settings field definitions
    getConfigurationDefinitions: (type: ConfigurationType, options?: ApiCallOptions) =>
        createConfigurationApiCall<ConfigurationDefinitions>('GET', `settings/${type}/definitions`)({...options}),

    // Font management
    getFonts: (params?: FontListParams, options?: ApiCallOptions) =>
        createConfigurationApiCall<PaginatedResponse<FontResource>>('GET', 'fonts')({...options, params}),

    getFont: (fontId: string, options?: ApiCallOptions) =>
        createConfigurationApiCall<FontResource>('GET', `fonts/${fontId}`)({...options}),

    createFont: (data: CreateFontRequest, options?: ApiCallOptions) => {
        const formData = new FormData();
        formData.append('name', data.name);
        formData.append('weight', data.weight);
        formData.append('file', data.file);
        return createConfigurationApiCall<FontResource>('POST', 'fonts')({...options, data: formData});
    },

    updateFont: (fontId: string, data: UpdateFontRequest, options?: ApiCallOptions) =>
        createConfigurationApiCall<FontResource>('PUT', `fonts/${fontId}`)({...options, data}),

    deleteFont: (fontId: string, options?: ApiCallOptions) =>
        createConfigurationApiCall<void>('DELETE', `fonts/${fontId}`)({...options}),

    downloadFont: (fontId: string, options?: ApiCallOptions) =>
        createConfigurationApiCall<Blob>('GET', `fonts/${fontId}/download`)({
            ...options,
            responseType: 'blob'
        }),

    // Account password / security settings.
    // GET returns the resource under a `{data}` envelope (like the other settings routes),
    // so callers read `result.data`. PUT is a partial MERGE: unsent keys are preserved,
    // so the body only needs the fields being changed. The PUT body is the FLAT settings
    // map (NOT wrapped in `{data}`) — that is the shape the service persists (a `{data}`
    // wrapper is accepted with 200 but silently ignored).
    getSecuritySettings: (account: string, options?: ApiCallOptions) =>
        createConfigurationApiCall<SecuritySettingsResource>('GET', getAccountSettingsEndpoint(account, 'security_settings'))({...options}),

    updateSecuritySettings: (account: string, settings: Partial<SecuritySettingsResource>, options?: ApiCallOptions) =>
        createConfigurationApiCall<SecuritySettingsResource>('PUT', getAccountSettingsEndpoint(account, 'security_settings'))({...options, data: settings}),

    // User security policy (uses S2S auth by default as it's called before user login).
    // Returns the password-complexity subset of the security settings.
    getUserSecurityPolicy: (userId: string, options?: ApiCallOptions) =>
        createConfigurationApiCall<Partial<SecuritySettingsResource>>('GET', `users/${userId}/security-policy`)({useS2SAuth: true, ...options})
};
